import type { SipStopRecommendation } from '../../../../api/sipPlan.types'
import { SipCategoryBucketKey } from '../../../../api/types'
import {
  PlansFundCard,
  type PlanFundCardCta
} from '../../../../../lumpsum/plans-list/lumpsum-ui-components/PlansFundCard'
import { useStopSipAction } from '../../../../../execution/sip/stop/usePlanStopSipAction'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { useOpenSipPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenSipPlanRecommendationBottomSheet'
import { useSipPlansContext } from '../../../sipPlansContext'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'
import {
  SipAmountBadge,
  SipFundCardActionLabel
} from './sipYourPlanFundCardShared'

type StopSipFundCardProps = {
  recommendation: SipStopRecommendation
  showBottomDivider?: boolean
}

export function StopSipFundCard({ recommendation, showBottomDivider = true }: StopSipFundCardProps) {
  const { plansOverview } = useSipPlansContext()
  const { openSipPlanRecommendationBottomSheet } = useOpenSipPlanRecommendationBottomSheet()
  const { onStopSip } = useStopSipAction()
  const totalIdealSipAmount = plansOverview?.totalIdealSipAmount ?? 0
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.SipYourPlan
  const fundRole = PlansFundRole.StopSip
  const currentSipAmount = recommendation?.currentSipAmount ?? 0
  const formattedCurrentSip = showAsFormattedRupeeAmount({ rawAmount: currentSipAmount })

  const openPlanBottomSheet = () => {
    openSipPlanRecommendationBottomSheet({
      isin,
      totalIdealSipAmount,
      bucketType: SipCategoryBucketKey.stopRecommendations,
      recommendation,
      manageSipCta: plansOverview?.manageSipCta
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const handleStopSip = () => {
    trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Stop SIP', fundName, fundIsin: isin })
    onStopSip(recommendation, plansOverview?.manageSipCta)
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-stop-sip`,
      label: 'Stop SIP',
      onClick: handleStopSip,
      variant: 'tertiary'
    },
    {
      id: `${isin}-learn-more`,
      label: 'Learn more',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<SipFundCardActionLabel label="Stop SIP" />}
      badgeRowExtras={currentSipAmount > 0 ? <SipAmountBadge currentSipAmount={formattedCurrentSip} /> : null}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}
